import { useState } from "react";
import { ArrowLeft, ChevronDown, Menu, X } from "lucide-react";
import { PersonIcon } from "./icons/patientListIcons";
import Button from "./ButtonComponent";
import { CSS_CLASSES } from "../constants/app";
import { Link, useLocation, useNavigate } from "react-router-dom";

interface NavbarProps {
  isLoggedIn?: boolean;
  userName?: string;
  onLogout?: () => void;
}

export default function Navbar({
  isLoggedIn = true,
  userName = "Dr. Gupta",
  onLogout,
}: NavbarProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Check if we're on the patient details page
  const isPatientDetailsPage = location.pathname.startsWith("/patient/");

  return (
    <nav className="bg-white border-b border-guardian-10 sticky top-0 z-30">
      <div className="mx-auto w-full max-w-full px-4 sm:px-6 lg:px-4">
        <div className="flex h-[72px] items-center justify-between gap-3">
          {/* Logo Section with Back Button */}
          <div className="flex items-center space-x-3 min-w-0">
            {isPatientDetailsPage && (
              <button
                onClick={() => navigate("/")}
                className="flex items-center gap-2 text-gray-700 hover:text-gray-900 transition-colors p-2 -ml-2 rounded-lg hover:bg-gray-100"
                aria-label="Back to home"
              >
                <ArrowLeft className="h-5 w-5" />
                <span className="text-sm font-medium hidden sm:inline">
                  Back
                </span>
              </button>
            )}
            <Link
              to="/"
              className="flex items-center space-x-3 cursor-pointer min-w-0"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <img
                src="/images/heart-logo.png"
                alt="Helius Logo"
                className="w-8 h-8"
              />
              <img
                src="/images/helius-text.png"
                alt="Helius"
                className="w-[85.33px] h-[16px]"
              />
            </Link>
          </div>

          {/* Desktop Right Section */}
          <div className="hidden md:flex items-center gap-3">
            {isLoggedIn ? (
              <>
                {/* User Menu */}
                <div className="relative max-w-[240px]">
                  <button
                    type="button"
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-full bg-guardian-10 hover:bg-guardian-20 text-xs font-semibold text-[#02040A] transition"
                  >
                    <PersonIcon className="h-4 w-4 shrink-0 text-guardian-80" />
                    <span className="truncate max-w-[140px]">{userName}</span>
                    <ChevronDown className="h-4 w-4 shrink-0" />
                  </button>

                  {/* Dropdown Menu */}
                  {isUserMenuOpen && (
                    <div
                      className={`${CSS_CLASSES.DROPDOWN_MENU.PANEL} ${CSS_CLASSES.DROPDOWN_MENU.PANEL_RIGHT}`}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          onLogout?.();
                          setIsUserMenuOpen(false);
                        }}
                        className={CSS_CLASSES.DROPDOWN_MENU.ITEM}
                      >
                        Log out
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <Button>Sign In</Button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button
              type="button"
              onClick={() => setIsMobileMenuOpen((open) => !open)}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
              aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 py-4">
            {isLoggedIn ? (
              <div className="space-y-3">
                <div className="px-4 py-2">
                  <span className="text-sm font-medium text-gray-700 truncate block">
                    {userName}
                  </span>
                </div>
                <div className="border-t border-gray-200 pt-3">
                  <button
                    type="button"
                    onClick={() => {
                      onLogout?.();
                      setIsMobileMenuOpen(false);
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Log out
                  </button>
                </div>
              </div>
            ) : (
              <div className="px-4">
                <Button className="w-full">Sign In</Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Overlay for dropdown */}
      {isUserMenuOpen && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setIsUserMenuOpen(false)}
        />
      )}
    </nav>
  );
}
